/**
 * GoogleDriveSettingsTab.jsx
 * Tab per gestione backup su Google Drive
 * 
 * AGGIORNATO: Wizard dettagliato per analisi e ripristino campo per campo
 */

import { useState, useEffect } from 'react'
import { 
  Cloud, 
  CloudOff, 
  CheckCircle2, 
  AlertCircle, 
  Loader2, 
  RefreshCw,
  Settings,
  Clock,
  Trash2,
  RotateCcw,
  Eye,
  FileJson,
  ExternalLink,
  Shield,
  ChevronLeft,
  ChevronRight,
  X,
  ArrowRight,
  User,
  FileText,
  Check
} from 'lucide-react'
import { gdriveApi } from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'

// Card component
const Card = ({ children, className = '' }) => (
  <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 ${className}`}>
    {children}
  </div>
)

// Toggle component
const Toggle = ({ checked, onChange, disabled = false }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!checked)}
    disabled={disabled}
    className={`
      relative inline-flex h-6 w-11 items-center rounded-full transition-colors
      ${checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}
      ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
    `}
  >
    <span className={`
      inline-block h-4 w-4 transform rounded-full bg-white transition-transform
      ${checked ? 'translate-x-6' : 'translate-x-1'}
    `} />
  </button>
)

// Progress bar per wizard
const ProgressBar = ({ current, total }) => {
  const percentage = total > 0 ? (current / total) * 100 : 0
  return (
    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
      <div 
        className="bg-primary-600 h-2 rounded-full transition-all duration-300"
        style={{ width: `${percentage}%` }}
      />
    </div>
  )
}

export default function GoogleDriveSettingsTab() {
  const { user, tenant } = useAuth()
  
  // Stati principali
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [backups, setBackups] = useState([])
  const [loadingBackups, setLoadingBackups] = useState(false)
  const [backupInProgress, setBackupInProgress] = useState(false)
  const [savingSettings, setSavingSettings] = useState(false)
  
  // Impostazioni locali
  const [localSettings, setLocalSettings] = useState({
    autoBackupEnabled: true,
    autoBackupTime: '03:00',
    frequency: 'daily',
    backupRetentionMonths: 12,
    maxBackupsToKeep: 30
  })
  
  // Modal states
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  
  // ========== WIZARD STATES ==========
  const [analyzeModal, setAnalyzeModal] = useState(null) // { loading, fileId, data }
  const [wizardMode, setWizardMode] = useState(null) // null | 'overview' | 'wizard' | 'summary'
  const [wizardItems, setWizardItems] = useState([])
  const [currentWizardIndex, setCurrentWizardIndex] = useState(0)
  const [selections, setSelections] = useState({}) // { [id]: { action: 'restore'|'skip', fields: { fieldName: 'backup'|'current' } } }
  const [restoreInProgress, setRestoreInProgress] = useState(false)
  
  // Toast/notification
  const [toast, setToast] = useState(null)
  
  const showSuccess = (msg) => setToast({ type: 'success', message: msg })
  const showError = (msg) => setToast({ type: 'error', message: msg })
  
  // Auto-hide toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000)
      return () => clearTimeout(timer)
    }
  }, [toast])
  
  // Piano Trial non può usare backup
  const isTrial = tenant?.currentPlan === 'trial'
  
  // Carica stato iniziale
  useEffect(() => {
    loadStatus()
  }, [])
  
  // Carica dopo connessione OAuth
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const gdriveStatus = params.get('gdrive')
    
    if (gdriveStatus === 'success') {
      showSuccess('Google Drive collegato con successo!')
      loadStatus()
      window.history.replaceState({}, '', window.location.pathname)
    } else if (gdriveStatus === 'error') {
      const error = params.get('error') || 'Errore sconosciuto'
      showError(`Errore connessione: ${error}`)
      window.history.replaceState({}, '', window.location.pathname)
    }
  }, [])
  
  const loadStatus = async () => {
    try {
      setLoading(true)
      const res = await gdriveApi.status()
      setStatus(res.data.data)
      
      if (res.data.data) {
        setLocalSettings({
          autoBackupEnabled: res.data.data.autoBackupEnabled ?? true,
          autoBackupTime: res.data.data.autoBackupTime || '03:00',
          frequency: res.data.data.frequency || 'daily',
          backupRetentionMonths: res.data.data.backupRetentionMonths || 12,
          maxBackupsToKeep: res.data.data.maxBackupsToKeep || 30
        })
        
        if (res.data.data.configured) {
          loadBackups()
        }
      }
    } catch (error) {
      console.error('Error loading gdrive status:', error)
    } finally {
      setLoading(false)
    }
  }
  
  const loadBackups = async () => {
    try {
      setLoadingBackups(true)
      const res = await gdriveApi.listBackups()
      // La risposta è { success, data: { backups: [...], count } }
      const data = res.data.data?.backups
      setBackups(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Error loading backups:', error)
      setBackups([])
    } finally {
      setLoadingBackups(false)
    }
  }
  
  const handleConnect = async () => {
    try {
      const res = await gdriveApi.getAuthUrl()
      window.location.href = res.data.data.authUrl
    } catch (error) {
      showError('Errore nella connessione')
    }
  }
  
  const handleDisconnect = async () => {
    if (!confirm('Sei sicuro di voler scollegare Google Drive?')) return
    
    try {
      await gdriveApi.disconnect()
      setStatus(prev => ({ ...prev, configured: false, connectedEmail: null }))
      setBackups([])
      showSuccess('Google Drive scollegato')
    } catch (error) {
      showError('Errore nella disconnessione')
    }
  }
  
  const handleSaveSettings = async () => {
    try {
      setSavingSettings(true)
      await gdriveApi.updateSettings(localSettings)
      showSuccess('Impostazioni backup salvate')
      setStatus(prev => ({ ...prev, ...localSettings }))
    } catch (error) {
      showError(error.response?.data?.message || 'Errore nel salvataggio')
    } finally {
      setSavingSettings(false)
    }
  }
  
  const handleManualBackup = async () => {
    try {
      setBackupInProgress(true)
      const res = await gdriveApi.backup()
      showSuccess(`Backup completato: ${res.data.data.stats.entriesCount} registrazioni salvate`)
      loadBackups()
    } catch (error) {
      if (error.response?.data?.code === 'RATE_LIMITED') {
        showError(error.response.data.message)
      } else {
        showError('Errore nel backup')
      }
    } finally {
      setBackupInProgress(false)
    }
  }
  
  // ========== WIZARD FUNCTIONS ==========
  
  const handleAnalyze = async (fileId) => {
    try {
      setAnalyzeModal({ loading: true, fileId })
      const res = await gdriveApi.analyze(fileId)
      const data = res.data.data
      
      setAnalyzeModal({ loading: false, fileId, data })
      setWizardMode('overview')
      setWizardItems(data.wizardItems || [])
      setCurrentWizardIndex(0)
      
      // Inizializza selezioni con default (mantieni attuale)
      const initialSelections = {}
      for (const item of data.wizardItems || []) {
        if (item.status === 'modified') {
          // Per elementi modificati, default: mantieni tutti i valori attuali
          const fieldDefaults = {}
          for (const diff of item.differences || []) {
            fieldDefaults[diff.field] = 'current'
          }
          initialSelections[item.id] = { action: 'restore', fields: fieldDefaults }
        } else if (item.status === 'deleted') {
          // Per elementi eliminati, default: non ripristinare
          initialSelections[item.id] = { action: 'skip', fields: {} }
        }
      }
      setSelections(initialSelections)
      
    } catch (error) {
      showError('Errore nell\'analisi del backup')
      setAnalyzeModal(null)
    }
  }
  
  const handleStartWizard = () => {
    setWizardMode('wizard')
    setCurrentWizardIndex(0)
  }
  
  const handleRestoreAll = async () => {
    if (!confirm('Sei sicuro di voler ripristinare TUTTI i dati dal backup? I dati attuali verranno sovrascritti.')) return
    
    try {
      setRestoreInProgress(true)
      const res = await gdriveApi.restore({ fileId: analyzeModal.fileId, mode: 'full' })
      showSuccess(`Ripristino completato: ${res.data.data.minoriRestored} minori, ${res.data.data.entriesRestored} registrazioni`)
      closeWizard()
    } catch (error) {
      showError('Errore nel ripristino')
    } finally {
      setRestoreInProgress(false)
    }
  }
  
  const handleFieldSelection = (itemId, fieldName, value) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        fields: {
          ...prev[itemId]?.fields,
          [fieldName]: value
        }
      }
    }))
  }
  
  const handleItemAction = (itemId, action) => {
    setSelections(prev => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        action
      }
    }))
  }
  
  const handleWizardNext = () => {
    if (currentWizardIndex < wizardItems.length - 1) {
      setCurrentWizardIndex(prev => prev + 1)
    } else {
      setWizardMode('summary')
    }
  }
  
  const handleWizardPrev = () => {
    if (currentWizardIndex > 0) {
      setCurrentWizardIndex(prev => prev - 1)
    }
  }
  
  const handleWizardSkip = () => {
    const item = wizardItems[currentWizardIndex]
    setSelections(prev => ({
      ...prev,
      [item.id]: { ...prev[item.id], action: 'skip' }
    }))
    handleWizardNext()
  }
  
  const handleFinalRestore = async () => {
    try {
      setRestoreInProgress(true)
      
      // Costruisci array selezioni per il backend
      const selectionsArray = Object.entries(selections).map(([id, sel]) => ({
        id,
        type: wizardItems.find(i => i.id.toString() === id.toString())?.type || 'entry',
        action: sel.action,
        fields: sel.fields
      }))
      
      const res = await gdriveApi.restore({ 
        fileId: analyzeModal.fileId, 
        mode: 'selective',
        selections: selectionsArray
      })
      
      showSuccess(`Ripristino completato: ${res.data.data.minoriRestored} minori, ${res.data.data.entriesRestored} registrazioni`)
      closeWizard()
    } catch (error) {
      showError('Errore nel ripristino')
    } finally {
      setRestoreInProgress(false)
    }
  }
  
  const closeWizard = () => {
    setAnalyzeModal(null)
    setWizardMode(null)
    setWizardItems([])
    setCurrentWizardIndex(0)
    setSelections({})
  }
  
  const handleDeleteBackup = async (fileId) => {
    try {
      await gdriveApi.deleteBackup(fileId)
      showSuccess('Backup eliminato')
      setBackups(prev => prev.filter(b => b.fileId !== fileId))
      setDeleteConfirm(null)
    } catch (error) {
      showError('Errore nell\'eliminazione')
    }
  }
  
  // Formattazione
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/D'
    return new Date(dateStr).toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    })
  }
  
  const formatSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return 'N/D'
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(2) + ' MB'
  }
  
  // Calcola riepilogo per summary
  const getSummaryStats = () => {
    let toRestore = 0
    let toSkip = 0
    let fieldsFromBackup = 0
    
    for (const item of wizardItems) {
      const sel = selections[item.id]
      if (sel?.action === 'skip') {
        toSkip++
      } else {
        if (item.status === 'deleted') {
          toRestore++
        } else if (item.status === 'modified' && sel?.fields) {
          const backupFields = Object.values(sel.fields).filter(v => v === 'backup').length
          if (backupFields > 0) {
            toRestore++
            fieldsFromBackup += backupFields
          } else {
            toSkip++
          }
        }
      }
    }
    
    return { toRestore, toSkip, fieldsFromBackup }
  }
  
  // Loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    )
  }
  
  // Trial plan
  if (isTrial) {
    return (
      <Card>
        <div className="text-center py-8">
          <CloudOff className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Backup non disponibile
          </h3>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            Il backup su Google Drive è disponibile dal piano Starter.
          </p>
          <a href="/subscription" className="btn-primary">
            Upgrade Piano
          </a>
        </div>
      </Card>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 p-4 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          {toast.message}
        </div>
      )}
      
      {/* Stato Connessione */}
      <Card>
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {status?.configured ? (
              <div className="h-12 w-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                <Cloud className="h-6 w-6 text-green-600" />
              </div>
            ) : (
              <div className="h-12 w-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <CloudOff className="h-6 w-6 text-gray-400" />
              </div>
            )}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Google Drive
              </h3>
              {status?.configured ? (
                <p className="text-sm text-green-600 dark:text-green-400 flex items-center">
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Connesso: {status.connectedEmail}
                </p>
              ) : (
                <p className="text-sm text-gray-500">Non connesso</p>
              )}
            </div>
          </div>
          
          <div>
            {status?.configured ? (
              <button onClick={handleDisconnect} className="btn-secondary text-red-600 hover:text-red-700">
                Disconnetti
              </button>
            ) : (
              <button onClick={handleConnect} className="btn-primary flex items-center">
                <Cloud className="h-4 w-4 mr-2" />
                Collega Google Drive
              </button>
            )}
          </div>
        </div>
        
        {!status?.configured && (
          <div className="mt-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-start space-x-3">
              <Shield className="h-5 w-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-700 dark:text-blue-300">
                <p className="font-medium mb-1">I tuoi dati restano tuoi</p>
                <p>I backup vengono salvati nel TUO Google Drive personale.</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Impostazioni e Backup (solo se connesso) */}
      {status?.configured && (
        <>
          {/* Backup Automatico */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-primary-600" />
              Backup Automatico
            </h3>
            
            <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">Backup automatico</p>
                  <p className="text-sm text-gray-500">Esegui backup secondo la programmazione</p>
                </div>
                <Toggle
                  checked={localSettings.autoBackupEnabled}
                  onChange={(checked) => setLocalSettings(prev => ({ ...prev, autoBackupEnabled: checked }))}
                />
              </div>

              {localSettings.autoBackupEnabled && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Orario backup
                    </label>
                    <input
                      type="time"
                      value={localSettings.autoBackupTime}
                      onChange={(e) => setLocalSettings(prev => ({ ...prev, autoBackupTime: e.target.value }))}
                      className="input w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Frequenza
                    </label>
                    <select
                      value={localSettings.frequency}
                      onChange={(e) => setLocalSettings(prev => ({ ...prev, frequency: e.target.value }))}
                      className="input w-full"
                    >
                      <option value="daily">Giornaliero</option>
                      <option value="weekly">Settimanale</option>
                      <option value="biweekly">Ogni 2 settimane</option>
                      <option value="monthly">Mensile</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Dati da includere
                    </label>
                    <select
                      value={localSettings.backupRetentionMonths || 'all'}
                      onChange={(e) => setLocalSettings(prev => ({ 
                        ...prev, 
                        backupRetentionMonths: e.target.value === 'all' ? null : parseInt(e.target.value) 
                      }))}
                      className="input w-full"
                    >
                      <option value="6">Ultimi 6 mesi</option>
                      <option value="12">Ultimi 12 mesi</option>
                      <option value="24">Ultimi 24 mesi</option>
                      <option value="all">Tutti i dati</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Backup da mantenere
                    </label>
                    <input
                      type="number"
                      min="5"
                      max="100"
                      value={localSettings.maxBackupsToKeep}
                      onChange={(e) => setLocalSettings(prev => ({ ...prev, maxBackupsToKeep: parseInt(e.target.value) }))}
                      className="input w-full"
                    />
                  </div>
                </div>
              )}
              
              <button
                onClick={handleSaveSettings}
                disabled={savingSettings}
                className="btn-primary"
              >
                {savingSettings ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Salva Impostazioni
              </button>
            </div>
          </Card>

          {/* Backup Manuale */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Cloud className="h-5 w-5 mr-2 text-primary-600" />
              Backup Manuale
            </h3>
            
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">
                  {status?.lastBackupDate 
                    ? `Ultimo backup: ${formatDate(status.lastBackupDate)}`
                    : 'Nessun backup recente'}
                </p>
              </div>
              <button
                onClick={handleManualBackup}
                disabled={backupInProgress}
                className="btn-primary"
              >
                {backupInProgress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Cloud className="h-4 w-4 mr-2" />
                )}
                Esegui Backup
              </button>
            </div>
          </Card>

          {/* Lista Backup */}
          <Card>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <FileJson className="h-5 w-5 mr-2 text-primary-600" />
              Backup Salvati
            </h3>
            
            {loadingBackups ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
              </div>
            ) : !Array.isArray(backups) || backups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileJson className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>Nessun backup trovato</p>
              </div>
            ) : (
              <div className="space-y-3">
                {backups.map((backup) => (
                  <div
                    key={backup.fileId}
                    className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
                  >
                    <div className="flex items-center space-x-3">
                      <FileJson className="h-8 w-8 text-primary-600" />
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">
                          {backup.fileName}
                        </p>
                        <p className="text-xs text-gray-500">
                          {formatDate(backup.createdAt)} • {formatSize(backup.fileSize)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleAnalyze(backup.fileId)}
                        className="p-2 text-gray-500 hover:text-primary-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                        title="Analizza e Ripristina"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(backup)}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600 rounded-lg"
                        title="Elimina"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {backups.length > 0 && (
              <button
                onClick={loadBackups}
                disabled={loadingBackups}
                className="mt-4 text-sm text-primary-600 hover:text-primary-700 flex items-center"
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${loadingBackups ? 'animate-spin' : ''}`} />
                Aggiorna lista
              </button>
            )}
          </Card>
        </>
      )}

      {/* ========== WIZARD MODALS ========== */}
      
      {/* Modal Overview - Riepilogo iniziale */}
      {analyzeModal && wizardMode === 'overview' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                📊 Analisi Backup
              </h3>
              <button onClick={closeWizard} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            {analyzeModal.loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
              </div>
            ) : (
              <div className="p-4 space-y-4 overflow-y-auto max-h-[60vh]">
                <p className="text-sm text-gray-500">
                  Backup del {formatDate(analyzeModal.data?.backupDate)}
                </p>
                
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{analyzeModal.data?.backupStats?.minoriCount || 0}</p>
                    <p className="text-xs text-blue-700 dark:text-blue-300">Minori</p>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{analyzeModal.data?.backupStats?.entriesCount || 0}</p>
                    <p className="text-xs text-green-700 dark:text-green-300">Registrazioni</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-3 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">{analyzeModal.data?.totalDifferences || 0}</p>
                    <p className="text-xs text-purple-700 dark:text-purple-300">Differenze</p>
                  </div>
                </div>
                
                {/* Differenze dettagliate */}
                {analyzeModal.data?.totalDifferences > 0 ? (
                  <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg space-y-2">
                    <p className="font-medium text-amber-800 dark:text-amber-200">📋 Differenze trovate:</p>
                    <div className="text-sm text-amber-700 dark:text-amber-300 space-y-1">
                      {analyzeModal.data?.minori?.modifiedCount > 0 && (
                        <p>• {analyzeModal.data.minori.modifiedCount} minori modificati</p>
                      )}
                      {analyzeModal.data?.minori?.addedInBackupCount > 0 && (
                        <p>• {analyzeModal.data.minori.addedInBackupCount} minori da ripristinare</p>
                      )}
                      {analyzeModal.data?.entries?.modifiedCount > 0 && (
                        <p>• {analyzeModal.data.entries.modifiedCount} registrazioni modificate</p>
                      )}
                      {analyzeModal.data?.entries?.addedInBackupCount > 0 && (
                        <p>• {analyzeModal.data.entries.addedInBackupCount} registrazioni da ripristinare</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <p className="text-green-700 dark:text-green-300">
                      ✓ I dati attuali sono identici al backup
                    </p>
                  </div>
                )}
                
                {/* Dati nuovi non nel backup */}
                {analyzeModal.data?.summary?.hasNewDataNotInBackup && (
                  <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-sm text-gray-600 dark:text-gray-300">
                    ℹ️ Ci sono {(analyzeModal.data?.minori?.addedInCurrentCount || 0) + (analyzeModal.data?.entries?.addedInCurrentCount || 0)} elementi nuovi non presenti nel backup
                  </div>
                )}
              </div>
            )}
            
            <div className="p-4 border-t dark:border-gray-700 space-y-2">
              {analyzeModal.data?.totalDifferences > 0 && (
                <>
                  <button
                    onClick={handleStartWizard}
                    className="btn-primary w-full flex items-center justify-center"
                  >
                    Esamina differenze
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </button>
                  <button
                    onClick={handleRestoreAll}
                    disabled={restoreInProgress}
                    className="btn-secondary w-full"
                  >
                    {restoreInProgress ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                    Ripristina tutto
                  </button>
                </>
              )}
              <button onClick={closeWizard} className="btn-secondary w-full">
                Chiudi
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Wizard Step - Elemento per elemento */}
      {analyzeModal && wizardMode === 'wizard' && wizardItems.length > 0 && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            {/* Header */}
            <div className="p-4 border-b dark:border-gray-700">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Differenza {currentWizardIndex + 1} di {wizardItems.length}
                </h3>
                <button onClick={closeWizard} className="text-gray-500 hover:text-gray-700">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <ProgressBar current={currentWizardIndex + 1} total={wizardItems.length} />
            </div>
            
            {/* Content */}
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {(() => {
                const item = wizardItems[currentWizardIndex]
                const itemSelection = selections[item.id] || {}
                
                return (
                  <div className="space-y-4">
                    {/* Item Header */}
                    <div className="flex items-center space-x-3 pb-3 border-b dark:border-gray-700">
                      {item.type === 'minore' ? (
                        <User className="h-8 w-8 text-blue-600" />
                      ) : (
                        <FileText className="h-8 w-8 text-green-600" />
                      )}
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-white">
                          {item.type === 'minore' ? 'MINORE' : 'REGISTRAZIONE'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {item.type === 'minore' 
                            ? item.nomeCompleto 
                            : `${item.minoreNome} - ${item.dataFormatted}`}
                        </p>
                      </div>
                    </div>
                    
                    {/* Elemento eliminato */}
                    {item.status === 'deleted' && (
                      <div className="space-y-4">
                        <div className="bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                          <p className="text-amber-700 dark:text-amber-300">
                            ⚠️ Questo elemento è stato eliminato dal database ma esiste nel backup
                          </p>
                        </div>
                        
                        {/* Preview dati */}
                        <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
                          <p className="font-medium text-gray-900 dark:text-white mb-2">Dati nel backup:</p>
                          <div className="text-sm space-y-1">
                            {item.preview && Object.entries(item.preview).map(([key, value]) => (
                              value && (
                                <p key={key} className="text-gray-600 dark:text-gray-300">
                                  <span className="font-medium">{key}:</span> {value}
                                </p>
                              )
                            ))}
                          </div>
                        </div>
                        
                        {/* Scelta */}
                        <div className="space-y-2">
                          <p className="font-medium text-gray-900 dark:text-white">Vuoi ripristinare?</p>
                          <div className="flex space-x-4">
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`action-${item.id}`}
                                checked={itemSelection.action === 'restore'}
                                onChange={() => handleItemAction(item.id, 'restore')}
                                className="text-primary-600"
                              />
                              <span className="text-green-600">Sì, ripristina</span>
                            </label>
                            <label className="flex items-center space-x-2 cursor-pointer">
                              <input
                                type="radio"
                                name={`action-${item.id}`}
                                checked={itemSelection.action === 'skip'}
                                onChange={() => handleItemAction(item.id, 'skip')}
                                className="text-primary-600"
                              />
                              <span className="text-gray-600">No, lascia eliminato</span>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Elemento modificato */}
                    {item.status === 'modified' && Array.isArray(item.differences) && item.differences.length > 0 && (
                      <div className="space-y-4">
                        <p className="text-sm text-gray-500">
                          Seleziona quale valore mantenere per ogni campo:
                        </p>
                        
                        {item.differences.map((diff) => (
                          <div key={diff.field} className="border dark:border-gray-700 rounded-lg overflow-hidden">
                            <div className="bg-gray-100 dark:bg-gray-700 px-3 py-2">
                              <p className="font-medium text-gray-900 dark:text-white">{diff.label}</p>
                            </div>
                            <div className="p-3 space-y-2">
                              {/* Opzione Backup */}
                              <label className={`flex items-start space-x-3 p-2 rounded cursor-pointer ${
                                itemSelection.fields?.[diff.field] === 'backup' 
                                  ? 'bg-blue-50 dark:bg-blue-900/30 border border-blue-300' 
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}>
                                <input
                                  type="radio"
                                  name={`field-${item.id}-${diff.field}`}
                                  checked={itemSelection.fields?.[diff.field] === 'backup'}
                                  onChange={() => handleFieldSelection(item.id, diff.field, 'backup')}
                                  className="mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-blue-600 font-medium">📦 BACKUP</p>
                                  <p className="text-sm text-gray-900 dark:text-white break-words">
                                    {diff.backupDisplay}
                                  </p>
                                </div>
                              </label>
                              
                              {/* Opzione Attuale */}
                              <label className={`flex items-start space-x-3 p-2 rounded cursor-pointer ${
                                itemSelection.fields?.[diff.field] === 'current' 
                                  ? 'bg-green-50 dark:bg-green-900/30 border border-green-300' 
                                  : 'hover:bg-gray-50 dark:hover:bg-gray-700'
                              }`}>
                                <input
                                  type="radio"
                                  name={`field-${item.id}-${diff.field}`}
                                  checked={itemSelection.fields?.[diff.field] === 'current'}
                                  onChange={() => handleFieldSelection(item.id, diff.field, 'current')}
                                  className="mt-1"
                                />
                                <div className="flex-1 min-w-0">
                                  <p className="text-xs text-green-600 font-medium">💾 ATTUALE</p>
                                  <p className="text-sm text-gray-900 dark:text-white break-words">
                                    {diff.currentDisplay}
                                  </p>
                                </div>
                              </label>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t dark:border-gray-700 flex justify-between">
              <button
                onClick={handleWizardPrev}
                disabled={currentWizardIndex === 0}
                className="btn-secondary flex items-center"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Indietro
              </button>
              
              <div className="flex space-x-2">
                <button
                  onClick={handleWizardSkip}
                  className="btn-secondary"
                >
                  Salta
                </button>
                <button
                  onClick={handleWizardNext}
                  className="btn-primary flex items-center"
                >
                  {currentWizardIndex === wizardItems.length - 1 ? 'Riepilogo' : 'Avanti'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Summary - Riepilogo finale */}
      {analyzeModal && wizardMode === 'summary' && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                ✅ Riepilogo Ripristino
              </h3>
              <button onClick={closeWizard} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh] space-y-4">
              {(() => {
                const stats = getSummaryStats()
                return (
                  <>
                    {/* Stats */}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-green-50 dark:bg-green-900/20 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-green-600">{stats.toRestore}</p>
                        <p className="text-xs text-green-700">Da ripristinare</p>
                      </div>
                      <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg text-center">
                        <p className="text-2xl font-bold text-gray-500">{stats.toSkip}</p>
                        <p className="text-xs text-gray-500">Saltati</p>
                      </div>
                    </div>
                    
                    {/* Dettaglio elementi */}
                    <div className="space-y-3">
                      {Array.isArray(wizardItems) && wizardItems.map((item) => {
                        const sel = selections[item.id]
                        const isSkipped = sel?.action === 'skip'
                        
                        // Per elementi modificati, conta campi da backup
                        let fieldsFromBackup = 0
                        if (item.status === 'modified' && sel?.fields) {
                          fieldsFromBackup = Object.values(sel.fields).filter(v => v === 'backup').length
                        }
                        
                        return (
                          <div key={item.id} className={`p-3 rounded-lg ${
                            isSkipped ? 'bg-gray-100 dark:bg-gray-700' : 'bg-green-50 dark:bg-green-900/20'
                          }`}>
                            <div className="flex items-center space-x-2">
                              {item.type === 'minore' ? (
                                <User className="h-4 w-4 text-blue-600" />
                              ) : (
                                <FileText className="h-4 w-4 text-green-600" />
                              )}
                              <span className="font-medium text-gray-900 dark:text-white text-sm">
                                {item.type === 'minore' ? item.nomeCompleto : `${item.minoreNome} - ${item.dataFormatted}`}
                              </span>
                            </div>
                            
                            <div className="mt-1 text-xs">
                              {isSkipped ? (
                                <span className="text-gray-500">⊘ Saltato</span>
                              ) : item.status === 'deleted' ? (
                                <span className="text-green-600">✓ Ripristina elemento</span>
                              ) : fieldsFromBackup > 0 ? (
                                <span className="text-green-600">✓ {fieldsFromBackup} campi dal backup</span>
                              ) : (
                                <span className="text-gray-500">Nessuna modifica</span>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </>
                )
              })()}
            </div>
            
            <div className="p-4 border-t dark:border-gray-700 space-y-2">
              <button
                onClick={handleFinalRestore}
                disabled={restoreInProgress}
                className="btn-primary w-full flex items-center justify-center"
              >
                {restoreInProgress ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Check className="h-4 w-4 mr-2" />
                )}
                Conferma e Ripristina
              </button>
              <button
                onClick={() => setWizardMode('wizard')}
                className="btn-secondary w-full"
              >
                ← Torna al wizard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Eliminazione */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-sm w-full p-6">
            <div className="text-center">
              <Trash2 className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                Elimina Backup
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Sei sicuro di voler eliminare questo backup?
              </p>
              <div className="flex space-x-3">
                <button onClick={() => setDeleteConfirm(null)} className="btn-secondary flex-1">
                  Annulla
                </button>
                <button
                  onClick={() => handleDeleteBackup(deleteConfirm.fileId)}
                  className="btn-danger flex-1"
                >
                  Elimina
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
